import React from "react";
import { useTranslation } from "react-i18next";
import { ArrowRight, Copy, Info } from "lucide-react";

import { cn } from "../../lib/utils";
import { formatCurrency, formatPaxRange, PricingConfig, QuoteLine } from "../../lib/pricing";
import { UseQuoteReturn } from "../../hooks/useQuote";
import { useNotification } from "../../contexts/NotificationContext";
import { Button } from "../ui/button";
import WhatsAppButton from "./WhatsAppButton";
import { Tone, tones } from "./tones";

interface Props {
  quote: UseQuoteReturn;
  config: PricingConfig;
  tone?: Tone;
  /** exibe o botão de WhatsApp e o copiar (false no simulador do admin) */
  showActions?: boolean;
  className?: string;
}

export function useQuoteLineLabel() {
  const { t } = useTranslation();
  return (line: QuoteLine) => {
    if (line.kind === "leg") {
      return t("quote.line.leg", {
        range: formatPaxRange(line.meta?.minPax ?? 1, line.meta?.maxPax ?? 1),
      });
    }
    if (line.kind === "roundTrip") {
      return t("quote.line.roundTrip", { multiplier: line.meta?.multiplier ?? 2 });
    }
    return line.meta?.perPax ? `${line.label} (${t("quote.perPax")})` : line.label;
  };
}

export default function QuoteSummary({
  quote,
  config,
  tone = "light",
  showActions = true,
  className,
}: Props) {
  const { t } = useTranslation();
  const { showNotification } = useNotification();
  const c = tones[tone];
  const lineLabel = useQuoteLineLabel();
  const { result, canSubmit, whatsappUrl, message } = quote;
  const isOk = result.status === "ok";

  const copy = async () => {
    try {
      await navigator.clipboard.writeText(message);
      showNotification("success", t("quote.copied"));
    } catch {
      showNotification("warning", t("quote.copyError"));
    }
  };

  return (
    <aside className={cn(c.summary, "space-y-4", className)} aria-live="polite">
      <div className="flex items-start justify-between gap-3">
        <div>
          <p className={c.eyebrow}>{t("quote.summary")}</p>
          {canSubmit ? (
            <p className={cn("mt-2 text-3xl font-extrabold tabular-nums", c.summaryTotal)}>
              {isOk ? formatCurrency(result.total, result.currency) : t("quote.onRequest")}
            </p>
          ) : (
            <p className={cn("mt-2 text-sm", c.muted)}>{t("quote.chooseDestination")}</p>
          )}
        </div>
        {canSubmit && (
          <span className={cn("rounded-full px-3 py-1 text-xs font-bold uppercase tracking-wide", isOk ? c.badge : c.badgeWarn)}>
            {isOk ? t("quote.estimate") : t("quote.onRequest")}
          </span>
        )}
      </div>

      {canSubmit && (result.origin || result.destination) && (
        <div className={cn("flex items-center gap-2 text-sm", c.text)}>
          {result.origin?.name && (
            <>
              <span className="truncate">{result.origin.name}</span>
              <ArrowRight className={cn("h-4 w-4 shrink-0", c.muted)} />
            </>
          )}
          <span className="truncate font-semibold">{result.destination?.name}</span>
        </div>
      )}

      {canSubmit && !isOk && result.reason && (
        <p className={cn("flex items-start gap-2 rounded-xl px-3 py-2 text-sm", c.badgeWarn)}>
          <Info className="mt-0.5 h-4 w-4 shrink-0" />
          <span>{t(`quote.reasons.${result.reason}`)}</span>
        </p>
      )}

      {isOk && (
        <dl className={cn("space-y-2 border-t pt-3 text-sm", c.divider)}>
          {result.lines.map((line, index) => (
            <div key={`${line.kind}-${index}`} className="flex items-center justify-between gap-3">
              <dt className={c.muted}>{lineLabel(line)}</dt>
              <dd className={cn("font-medium tabular-nums", c.text)}>
                {formatCurrency(line.amount, result.currency)}
              </dd>
            </div>
          ))}
          <div className={cn("flex items-center justify-between border-t pt-2 font-bold", c.divider, c.text)}>
            <dt>{t("quote.total")}</dt>
            <dd className="tabular-nums">{formatCurrency(result.total, result.currency)}</dd>
          </div>
        </dl>
      )}

      {canSubmit && (
        <p className={cn("flex items-start gap-2 text-xs", c.muted)}>
          <Info className="mt-0.5 h-3.5 w-3.5 shrink-0" />
          <span>{config.disclaimer || t("quote.disclaimerDefault")}</span>
        </p>
      )}

      {showActions && (
        <div className="space-y-2">
          <WhatsAppButton href={whatsappUrl} disabled={!canSubmit}>
            {t("quote.whatsapp")}
          </WhatsAppButton>
          <Button
            type="button"
            variant="ghost"
            disabled={!canSubmit}
            onClick={copy}
            className={cn("w-full", tone === "dark" && "text-white/80 hover:bg-white/10 hover:text-white")}
          >
            <Copy className="h-4 w-4" /> {t("quote.copy")}
          </Button>
        </div>
      )}
    </aside>
  );
}
